import React, { useState, useEffect } from 'react';

interface Occupation {
  id: number;
  name: string;
}

interface UserProfileOccupation {
  id: number;
  occupation_id: number;
  years_of_experience: number;
  occupation: Occupation;
}

interface UserProfileOccupationsPageProps {
  initialOccupations?: UserProfileOccupation[];
  allOccupations?: Occupation[];
  isUserSignedIn?: boolean;
}

const UserProfileOccupationsPage: React.FC<UserProfileOccupationsPageProps> = ({
  initialOccupations = [],
  allOccupations = [],
  isUserSignedIn = false
}) => {
  const [userProfileOccupations, setUserProfileOccupations] = useState<UserProfileOccupation[]>(initialOccupations);
  const [allOccupationsList, setAllOccupationsList] = useState<Occupation[]>(allOccupations);
  const [isLoading, setIsLoading] = useState(false);
  const [newOccupationId, setNewOccupationId] = useState<number | ''>('');
  const [newYearsOfExperience, setNewYearsOfExperience] = useState<number>(1);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingOccupationId, setEditingOccupationId] = useState<number | ''>('');
  const [editingYears, setEditingYears] = useState<number>(1);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    if (isUserSignedIn) {
      fetchUserProfileOccupations();
      fetchAllOccupations();
    }
  }, [isUserSignedIn]);

  // 職種一覧を取得
  const fetchUserProfileOccupations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/mypage/user_profile_occupations.json', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfileOccupations(data.user_profile_occupations || []);
      } else {
        console.error('Failed to fetch user profile occupations:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user profile occupations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 全職種一覧を取得
  const fetchAllOccupations = async () => {
    try {
      const response = await fetch('/api/v1/occupations', {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllOccupationsList(data);
      }
    } catch (error) {
      console.error('Error fetching all occupations:', error);
    }
  };

  // 職種を追加
  const handleCreate = async () => {
    if (!newOccupationId || newOccupationId === '') return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/mypage/user_profile_occupations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile_occupation: {
            occupation_id: newOccupationId,
            years_of_experience: newYearsOfExperience
          }
        })
      });

      if (response.ok) {
        await fetchUserProfileOccupations();
        setNewOccupationId('');
        setNewYearsOfExperience(1);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('職種を追加しました', 'success', false);
        }
      } else {
        const errorData = await response.json();
        if (window.showGlobalFlash) {
          window.showGlobalFlash(errorData.errors?.join(', ') || '職種の追加に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 職種を更新
  const handleUpdate = async (id: number) => {
    if (!editingOccupationId || editingOccupationId === '') return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/mypage/user_profile_occupations/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile_occupation: {
            occupation_id: editingOccupationId,
            years_of_experience: editingYears
          }
        })
      });

      if (response.ok) {
        await fetchUserProfileOccupations();
        setEditingId(null);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('職種を更新しました', 'success', false);
        }
      } else {
        const errorData = await response.json();
        if (window.showGlobalFlash) {
          window.showGlobalFlash(errorData.errors?.join(', ') || '職種の更新に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 職種を削除
  const handleDelete = async (id: number) => {
    if (!confirm('本当に削除しますか？')) return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/mypage/user_profile_occupations/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        }
      });

      if (response.ok) {
        await fetchUserProfileOccupations();
        if (window.showGlobalFlash) {
          window.showGlobalFlash('職種を削除しました', 'success', false);
        }
      } else {
        if (window.showGlobalFlash) {
          window.showGlobalFlash('職種の削除に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 編集開始
  const startEditing = (occupation: UserProfileOccupation) => {
    setEditingId(occupation.id);
    setEditingOccupationId(occupation.occupation_id);
    setEditingYears(occupation.years_of_experience);
  };

  // 編集キャンセル
  const cancelEditing = () => {
    setEditingId(null);
    setEditingOccupationId('');
    setEditingYears(1);
  };

  // ログインしていない場合の表示
  if (!isUserSignedIn) {
    return (
      <div className="container p-4">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="h3 fw-bold mb-4">職種管理</h1>
          <p className="text-muted mb-4">
            新規アカウントを登録すると、自分に合ったスキルや職種の案件を選択できます。
            自分に合った案件を見つけることができます。
          </p>
          <a href="/users/sign_up" className="btn btn-primary me-2">
            無料登録
          </a>
          <a href="/users/sign_in" className="btn btn-outline-primary">
            ログイン
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container p-4">
      <h2 className="mb-4">職種管理</h2>

      {/* 新規追加フォーム */}
      <div className="card mb-4">
        <div className="card-header">新しい職種を追加</div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">職種</label>
            <select
              className="form-select"
              value={newOccupationId}
              onChange={(e) => setNewOccupationId(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">選択してください</option>
              {allOccupationsList.map((occupation) => (
                <option key={occupation.id} value={occupation.id}>
                  {occupation.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">経験年数</label>
            <input
              type="number"
              className="form-control"
              min="0"
              value={newYearsOfExperience}
              onChange={(e) => setNewYearsOfExperience(parseInt(e.target.value) || 0)}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={isLoading || !newOccupationId}
          >
            職種を追加
          </button>
        </div>
      </div>

      {/* 既存職種一覧 */}
      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th>職種</th>
            <th>経験年数</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {userProfileOccupations.map((ups) => (
            <tr key={ups.id}>
              <td>
                {editingId === ups.id ? (
                  <select
                    className="form-select d-inline-block w-auto"
                    value={editingOccupationId}
                    onChange={(e) => setEditingOccupationId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    {allOccupationsList.map((occupation) => (
                      <option key={occupation.id} value={occupation.id}>
                        {occupation.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  ups.occupation.name
                )}
              </td>
              <td>
                {editingId === ups.id ? (
                  <input
                    type="number"
                    className="form-control d-inline-block w-auto"
                    min="0"
                    value={editingYears}
                    onChange={(e) => setEditingYears(parseInt(e.target.value) || 0)}
                  />
                ) : (
                  ups.years_of_experience
                )}
              </td>
              <td className="text-end">
                {editingId === ups.id ? (
                  <>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleUpdate(ups.id)}
                      disabled={isLoading}
                    >
                      更新する
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={cancelEditing}
                    >
                      キャンセル
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => startEditing(ups)}
                    >
                      編集
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(ups.id)}
                      disabled={isLoading}
                    >
                      削除する
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserProfileOccupationsPage;

